// Cierre del wizard: lo cobrado hoy, lo financiado, los productos y los códigos y bonos de la venta.
// Una derivada sin extras no se celebra: queda en espera de la financiera.
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, Check, ExternalLink, Home, Plus } from 'lucide-react';
import { Button, CopyableId, Loading } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import {
    useContractPreview,
    useDeviceEnrollment,
    useFinanciers,
    useWelcomeBonus,
    useWizardSession,
} from '../api/useWizard';
import { externalProviderName, isPausedForReferral } from '../externalReferral';
import { linePhoneCount } from '../productSuggestions';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { saleRegistrationMissing } from '../saleRegistration';
import { chargeLinesOf, linesTotalCents, type ChargeLine } from '../saleCharge';
import {
    LineName,
    LinePromotionBadge,
    LineSubtitle,
    type LineSlots,
} from '../components/LineLabels';
import { creditTermsSummaryOf, type CreditTermsSummary } from '../creditTermsSummary';
import {
    appliedCodesOf,
    closingBonusesOf,
    creditPromotionBonusesOf,
    creditPromotionBonusRetrying,
    type AppliedCode,
    type ClosingBonus,
} from '../saleClosing';
import { StateIcon } from '../components/KycScreenBits';
import { Confetti } from '../components/Confetti';
import { PromotionBonusItem } from '../components/PromotionBonusItem';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { NO_CHARGE_LABEL, registeredMethodsLabel } from '../payments';
import type { RegisteredPayment } from '../types';

const mxn = (cents: number): string => formatMXN(cents / 100);

/** Lo que recibe el cupón de Promociones montado en el cierre. */
export interface CouponSlotContext {
    sessionId: string;
    saleId: string;
}

/** Lugares para las piezas de Promociones en el cierre (REQ-134); sin pieza no dejan hueco. */
export interface SummarySlots {
    /** Cupón que la venta le da al cliente; solo con la venta registrada. */
    coupon?: (ctx: CouponSlotContext) => ReactNode;
    /** Nombres de regalos y promos en el desglose de líneas. */
    lines?: LineSlots;
}

export function SummaryStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: SummarySlots;
}) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const external = session?.paymentMethodKind === 'EXTERNAL';
    const isCredit = session?.paymentMethodKind === 'SUREKEEP';
    // El catálogo de financieras solo hace falta para nombrar la externa.
    const { data: financiers } = useFinanciers(external ? sessionId : '');
    const creditId = session?.loan ? session.loan.creditId : undefined;
    const preview = useContractPreview(sessionId, isCredit && creditId !== undefined);
    const welcomeApplied =
        session?.welcome.welcomeAccredited === true || session?.welcome.topupApplied === true;
    const welcomeBonus = useWelcomeBonus(sessionId, welcomeApplied);
    // Mientras el bono de crédito siga RESERVED, el resumen mantiene el poll que lo reintenta.
    const enrollment = useDeviceEnrollment(
        sessionId,
        creditPromotionBonusRetrying(isCredit, session),
    );
    if (!session) return <Loading size="lg" />;

    // El silo usa el sessionId como saleId; a variable local (regla anti-UUID mira props `*Id`).
    const saleRef = session.saleId;
    // Sin referencia hay dos casos opuestos: el registro falló, o la venta se derivó y nunca lo tuvo.
    const registrationMissing = saleRegistrationMissing(session);
    const derived = !saleRef && !registrationMissing;
    const hasLines = session.reservedLines !== undefined;
    const lines = chargeLinesOf(session, { credit: isCredit, ...lineInfo });
    // Derivada sin extras: el silo la pausa en espera de la financiera y aquí no hubo cobro.
    const pausedReferral = isPausedForReferral(session);
    const providerName = externalProviderName(session.externalProvider, financiers);
    const referredLineIds = new Set(
        (session.reservedLines ?? []).filter((l) => linePhoneCount(l) > 0).map((l) => l.lineId),
    );
    const isReferred = (l: ChargeLine): boolean =>
        external && l.lineId !== null && referredLineIds.has(l.lineId);
    const credit = isCredit ? preview.data?.credit : undefined;
    const terms = credit ? creditTermsSummaryOf(credit) : null;
    // El código usado en ESTA venta va en su línea, con lo que descontó.
    const usedCodes = new Map(appliedCodesOf(session, lines).map((c) => [c.key, c]));
    const creditPromotionBonus =
        enrollment.data?.creditPromotionBonus ?? session.creditPromotionBonus;
    const bonuses = closingBonusesOf(
        session,
        welcomeBonus.data?.amountCents,
        creditPromotionBonusesOf(creditPromotionBonus ? { creditPromotionBonus } : {}),
    );
    const celebrate = !registrationMissing && !pausedReferral;
    const customer = session.kyc.verified?.fullName;

    return (
        // `isolate` encierra el confeti: brota detrás de la tarjeta sin quedar detrás de la página.
        <div className="isolate w-full max-w-[680px] lg:max-w-[720px]">
            {/* Solo sobre una venta que de verdad cerró: si falta registrarla no hay qué celebrar. */}
            {celebrate && <Confetti pieces={140} origin="behind" />}
            <WizardCard>
                <WizardCardHead
                    eyebrow="Cierre"
                    title={headTitle(registrationMissing, pausedReferral, providerName)}
                    sub={
                        pausedReferral
                            ? `Completa el crédito en el sistema de ${providerName}. La venta queda en espera de la financiera.`
                            : headSub(saleRef, derived)
                    }
                />

                <WizardCardBody>
                    <div className="text-center">
                        <StateIcon
                            tone={
                                registrationMissing
                                    ? 'warning'
                                    : pausedReferral
                                      ? 'info'
                                      : 'success'
                            }
                        >
                            {registrationMissing ? (
                                <AlertTriangle className="size-7" aria-hidden />
                            ) : pausedReferral ? (
                                <ExternalLink className="size-7" aria-hidden />
                            ) : (
                                <Check className="size-7" strokeWidth={3} aria-hidden />
                            )}
                        </StateIcon>
                        <div className="text-[16px] font-semibold">
                            {registrationMissing
                                ? 'La venta no quedó registrada'
                                : pausedReferral
                                  ? `En espera de ${providerName}`
                                  : 'Venta registrada'}
                        </div>
                        {customer && (
                            <p className="mt-0.5 text-[12.5px] text-muted-foreground">{customer}</p>
                        )}
                    </div>

                    {!pausedReferral && (
                        <MoneyTiles
                            todayCents={session.payment?.amountCents ?? null}
                            todayNote={todayNote(session.payment, isCredit)}
                            terms={terms}
                            totalCents={hasLines ? linesTotalCents(lines) : null}
                        />
                    )}

                    {hasLines && lines.length > 0 && (
                        <ProductList
                            lines={lines}
                            tagOf={(l) => (isReferred(l) ? `Con ${providerName}` : paymentTagOf(l))}
                            usedCodeOf={(l) =>
                                l.lineId === null ? undefined : usedCodes.get(l.lineId)
                            }
                            slots={slots.lines}
                        />
                    )}
                    {!hasLines && session.product && (
                        <p className="mx-auto mt-4 max-w-md text-[12.5px]">
                            {catalogDisplayName(session.product, lineInfo.descriptions)}
                        </p>
                    )}
                    <CodesAndBonuses
                        earned={
                            slots.coupon && saleRef
                                ? slots.coupon({ sessionId, saleId: saleRef })
                                : null
                        }
                        bonuses={bonuses}
                    />

                    {/* 🔴 Sin referencia no hay `Sale`, ni evento a La Central, ni comisión. Se dice
                    entero, y con la salida al paso donde vive el reintento. */}
                    {registrationMissing && (
                        <p className="mx-auto mt-3.5 max-w-md rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
                            La venta no se registró. Regresa a Entrega.
                        </p>
                    )}
                    {!pausedReferral && session.folio && (
                        <div className="mx-auto mt-4 flex max-w-md justify-center text-[11px] text-muted-foreground">
                            <CopyableId label="Folio" value={session.folio} inline />
                        </div>
                    )}
                </WizardCardBody>

                <WizardFootNav
                    showExit={false}
                    back={
                        <Button
                            variant="ghost"
                            leftIcon={<Home className="size-3.5" aria-hidden />}
                            onClick={() => void navigate({ to: '/sales' })}
                        >
                            Ir al portal
                        </Button>
                    }
                >
                    {registrationMissing ? (
                        <Button
                            leftIcon={<ArrowLeft className="size-3.5" aria-hidden />}
                            onClick={() =>
                                void navigate({
                                    to: '/wizard/s/$sessionId/$step',
                                    params: { sessionId, step: 'delivery' },
                                })
                            }
                        >
                            Volver a la entrega
                        </Button>
                    ) : (
                        <Button
                            leftIcon={<Plus className="size-3.5" aria-hidden />}
                            onClick={() => void navigate({ to: '/wizard' })}
                        >
                            Nueva venta
                        </Button>
                    )}
                </WizardFootNav>
            </WizardCard>
        </div>
    );
}

/** Lo cobrado hoy y lo financiado, lado a lado, con la cuenta que los une al precio. */
function MoneyTiles({
    todayCents,
    todayNote,
    terms,
    totalCents,
}: {
    todayCents: number | null;
    todayNote: string;
    terms: CreditTermsSummary | null;
    /** «Total de la venta»; la cuenta solo se muestra si hoy + a crédito da exactamente eso. */
    totalCents: number | null;
}) {
    const adds =
        terms !== null &&
        todayCents !== null &&
        totalCents !== null &&
        todayCents + terms.financedCents === totalCents;
    return (
        <div className="mx-auto mt-5 flex max-w-md flex-col gap-2.5 text-left">
            {adds && (
                <p
                    data-testid="closing-equation"
                    className="text-center text-[12px] text-muted-foreground tabular-nums"
                >
                    Precio {mxn(totalCents)} = {mxn(todayCents)} hoy + {mxn(terms.financedCents)} a
                    crédito
                </p>
            )}
            <div className={terms ? 'grid grid-cols-1 gap-2.5 sm:grid-cols-2' : 'grid'}>
                <section
                    aria-label="Pagó hoy"
                    data-testid="closing-today"
                    className="flex flex-col gap-1 rounded-[14px] border border-border bg-card p-4"
                >
                    <span className="text-[12px] font-semibold text-muted-foreground">
                        Pagó hoy
                    </span>
                    <span className="text-[28px] font-bold leading-tight tabular-nums">
                        {todayCents === null ? '—' : mxn(todayCents)}
                    </span>
                    <span className="text-[12px] text-muted-foreground">{todayNote}</span>
                </section>
                {terms && (
                    <section
                        aria-label="A crédito"
                        data-testid="closing-credit"
                        className="flex flex-col gap-1 rounded-[14px] border border-promo-soft-border bg-promo-soft p-4"
                    >
                        <span className="text-[12px] font-semibold text-promo">A crédito</span>
                        <span className="text-[28px] font-bold leading-tight text-promo tabular-nums">
                            {mxn(terms.financedCents)}
                        </span>
                        <span className="text-[12px] text-promo tabular-nums">
                            {terms.weeks} pagos de {mxn(terms.weeklyCents)}
                            {terms.lastCents !== null && `, el último de ${mxn(terms.lastCents)}`}
                        </span>
                        <span className="text-[12px] text-promo tabular-nums">
                            Total del crédito {mxn(terms.totalCents)}
                        </span>
                    </section>
                )}
            </div>
        </div>
    );
}

/** Productos de la venta: nombre, etiqueta de cómo se pagó y precio; el total al final. */
function ProductList({
    lines,
    tagOf,
    usedCodeOf,
    slots,
}: {
    lines: ChargeLine[];
    tagOf: (line: ChargeLine) => string;
    /** Código de descuento usado en esta venta sobre la línea. */
    usedCodeOf: (line: ChargeLine) => AppliedCode | undefined;
    slots?: LineSlots;
}) {
    return (
        <div className="mx-auto mt-5 max-w-md text-left">
            <ul
                aria-label="Productos de la venta"
                className="flex flex-col divide-y divide-border/60"
            >
                {lines.map((line) => {
                    const tag = tagOf(line);
                    const credit = tag.startsWith('Crédito');
                    const used = usedCodeOf(line);
                    return (
                        <li
                            key={line.key}
                            data-testid="closing-line"
                            className="flex items-start justify-between gap-3 py-2 text-[13px]"
                        >
                            <span className="min-w-0">
                                <LineName line={line} {...(slots && { slots })} />
                                {line.qty > 1 && ` × ${String(line.qty)}`}
                                <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                    <span
                                        data-testid="closing-line-tag"
                                        className={
                                            credit
                                                ? 'rounded-full bg-promo-soft px-2 py-0.5 text-[10.5px] font-semibold text-promo'
                                                : 'rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground'
                                        }
                                    >
                                        {tag}
                                    </span>
                                    {line.gift ? (
                                        <span className="text-[11px] text-muted-foreground">
                                            <LineSubtitle line={line} {...(slots && { slots })} />
                                        </span>
                                    ) : (
                                        <LinePromotionBadge
                                            line={line}
                                            discounted={line.promotion !== undefined}
                                            {...(slots && { slots })}
                                        />
                                    )}
                                </span>
                                {used && (
                                    <span
                                        data-testid="closing-used-code"
                                        className="mt-0.5 block text-[11.5px] text-success tabular-nums"
                                    >
                                        {used.discountCents !== null &&
                                            `−${mxn(used.discountCents)} · `}
                                        <span className="font-mono font-semibold">{used.code}</span>
                                    </span>
                                )}
                            </span>
                            <span className="shrink-0 tabular-nums">
                                {line.totalCents === null ? '—' : mxn(line.totalCents)}
                            </span>
                        </li>
                    );
                })}
            </ul>
            <div
                data-testid="closing-total"
                className="flex items-baseline justify-between gap-3 border-t border-border pt-2.5"
            >
                <span className="text-[13px] font-semibold">Total de la venta</span>
                <span className="text-[18px] font-bold tabular-nums">
                    {mxn(linesTotalCents(lines))}
                </span>
            </div>
        </div>
    );
}

/** «Crédito», «Contado» o, si la línea se partió, las dos. */
function paymentTagOf(line: ChargeLine): string {
    if (line.financedCents > 0 && (line.cashCents ?? 0) > 0) return 'Crédito y contado';
    return line.financedCents > 0 ? 'Crédito' : 'Contado';
}

/** Cómo pagó hoy: los medios del cobro o «Sin cobro» en uno de $0; en crédito agrega «· enganche». */
function todayNote(payment: RegisteredPayment | undefined, isCredit: boolean): string {
    const label = payment ? (registeredMethodsLabel(payment) ?? NO_CHARGE_LABEL) : '—';
    return isCredit ? `${label} · enganche` : label;
}

/** Códigos aplicados y bonos de la venta, uno por línea con su estado; sin ninguno no se pinta. */
export function CodesAndBonuses({
    earned,
    bonuses,
}: {
    /** Pieza de Promociones con los códigos para la próxima compra; pinta nada si no hay. */
    earned?: ReactNode;
    bonuses: ClosingBonus[];
}) {
    if (!earned && bonuses.length === 0) return null;
    return (
        <div className="mx-auto mt-3.5 flex max-w-md flex-col gap-2 text-left">
            {earned && (
                <div data-testid="slot-coupon" className="empty:hidden">
                    {earned}
                </div>
            )}
            {bonuses.length > 0 && (
                <ul aria-label="Bonos de la venta" className="flex flex-col gap-2">
                    {bonuses.map((b) => (
                        <PromotionBonusItem
                            key={b.key}
                            kind={b.kind}
                            label={b.label}
                            amountCents={b.amountCents}
                            displayStatus={b.displayStatus}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Título del cierre: registro faltante, venta pasada a la financiera o venta completada. */
function headTitle(
    registrationMissing: boolean,
    pausedReferral: boolean,
    provider: string,
): string {
    if (registrationMissing) return 'Falta registrar la venta';
    if (pausedReferral) return `Venta pasada a ${provider}`;
    return '¡Venta completada!';
}

/** Qué dice el subtítulo: la referencia, la derivación, o que el registro falta. */
function headSub(saleRef: string | undefined, derived: boolean): string {
    if (saleRef) return `Referencia de venta ${saleRef}`;
    if (derived) return 'La venta se derivó a una financiera externa.';
    return 'Se terminó el proceso, pero la venta no llegó a registrarse.';
}
